import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  ChevronLeft,
  ChevronRight,
  Send,
  Save,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Image as ImageIcon,
  Layers,
  GitBranch,
  User,
  MessageSquare,
  RefreshCcw,
  Eye,
  Download,
  Maximize2,
  Sliders,
  Sparkles,
  ArrowRight,
  ExternalLink,
  Copy,
  Settings,
  BarChart3,
  Droplets,
  Zap,
  Gem
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProposalWorkspace
 * =================
 * Premium multi-panel professional workspace for A&D professionals.
 * Integrates SRE renders with proposal workflow.
 * 
 * Layout:
 * - LEFT: Project info, client, materials, advisor
 * - CENTER: Render viewer, before/after, version timeline
 * - RIGHT: Material DNA, quality metrics, actions
 * 
 * Dark cinematic luxury UI.
 */
export default function ProposalWorkspace() {
  const { proposalId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Data states
  const [proposal, setProposal] = useState(null);
  const [renders, setRenders] = useState([]);
  const [versions, setVersions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [comments, setComments] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeRenderIndex, setActiveRenderIndex] = useState(0);
  const [showBefore, setShowBefore] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  // Panel collapse states
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  
  // Load proposal data
  useEffect(() => {
    console.log('[ProposalWorkspace] useEffect triggered', { proposalId, hasUser: !!user });
    if (proposalId) {
      loadProposalData();
    }
  }, [proposalId, user]); // Added user to dependencies
  
  const loadProposalData = async () => {
    console.log('[ProposalWorkspace] loadProposalData called', { proposalId, hasToken: !!token });
    try {
      setLoading(true);
      
      // Fetch proposal details
      console.log('[ProposalWorkspace] Fetching proposal:', `${API_URL}/api/proposals/${proposalId}`);
      const proposalRes = await fetch(`${API_URL}/api/proposals/${proposalId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      console.log('[ProposalWorkspace] Proposal response:', proposalRes.status);
      
      if (!proposalRes.ok) {
        if (proposalRes.status === 404) {
          throw new Error('Proposta non trovata');
        }
        throw new Error('Errore nel caricamento');
      }
      
      const proposalData = await proposalRes.json();
      console.log('[ProposalWorkspace] Proposal loaded:', proposalData.proposal?.title);
      setProposal(proposalData.proposal || proposalData);
      
      // Fetch renders for this proposal
      console.log('[ProposalWorkspace] Fetching renders...');
      const rendersRes = await fetch(`${API_URL}/api/sre-proposal/renders/proposal/${proposalId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      console.log('[ProposalWorkspace] Renders response:', rendersRes.status);
      
      if (rendersRes.ok) {
        const rendersData = await rendersRes.json();
        console.log('[ProposalWorkspace] Renders loaded:', rendersData.total);
        setRenders(rendersData.renders || []);
      }
      
      // Fetch advisors for material (if we have renders)
      if (proposalData.material_id) {
        const advisorsRes = await fetch(`${API_URL}/api/ail/advisors/for-material/${proposalData.material_id}?limit=3`);
        if (advisorsRes.ok) {
          const advisorsData = await advisorsRes.json();
          setAdvisors(advisorsData.advisors || []);
        }
      }
      
      console.log('[ProposalWorkspace] Load complete');
      
    } catch (err) {
      console.error('[ProposalWorkspace] Load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/proposals/${proposalId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          status: 'draft',
          updated_at: new Date().toISOString()
        })
      });
      
      if (!response.ok) throw new Error('Errore nel salvataggio');
      
      toast.success('Bozza salvata');
      loadProposalData();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSendToClient = async () => {
    if (!window.confirm('Inviare la proposta al cliente?')) return;
    
    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/proposals/${proposalId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) throw new Error('Errore nell\'invio');
      
      const data = await response.json();
      toast.success('Proposta inviata al cliente!');
      
      // Copy link to clipboard
      if (data.client_link) {
        await navigator.clipboard.writeText(data.client_link);
        toast.info('Link copiato negli appunti');
      }
      
      loadProposalData();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSending(false);
    }
  };
  
  const handleCopyClientLink = async () => {
    if (!proposal?.access_token) {
      toast.error('Proposta non ancora inviata');
      return;
    }
    
    const link = `${window.location.origin}/client/proposal/${proposal.access_token}`;
    await navigator.clipboard.writeText(link);
    toast.success('Link cliente copiato');
  };
  
  const handleCreateNewVersion = async () => {
    const currentRender = renders[activeRenderIndex];
    if (!currentRender) {
      toast.error('Seleziona prima un render');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/sre-proposal/renders/${currentRender.id}/create-version`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          version_type: 'revision',
          change_reason: 'Nuova versione'
        })
      });
      
      if (!response.ok) throw new Error('Errore nella creazione versione');
      
      toast.success('Nuova versione creata');
      loadProposalData();
      
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const currentRender = renders[activeRenderIndex];
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#B08B5C] animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Caricamento workspace...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-light text-white mb-2">Errore</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/proposals')}
            className="px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
          >
            Torna alle proposte
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      
      {/* Header */}
      <header className="flex-shrink-0 border-b border-zinc-800/50 bg-[#0A0A0B]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-4">
              <Link 
                to="/proposals"
                className="p-2 rounded-lg hover:bg-zinc-800/50 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-zinc-400" />
              </Link>
              <div>
                <h1 className="text-lg font-medium">{proposal?.title || 'Proposta'}</h1>
                <div className="flex items-center gap-3 text-sm text-zinc-500">
                  <span>{proposal?.client_name || proposal?.client_email}</span>
                  {proposal?.project_name && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-zinc-600" />
                      <span>{proposal.project_name}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Center: Status */}
            <StatusBadge status={proposal?.status} />
            
            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {proposal?.access_token && (
                <button
                  onClick={handleCopyClientLink}
                  className="px-4 py-2 bg-zinc-800 border border-zinc-700 text-sm rounded-lg hover:bg-zinc-700 flex items-center gap-2"
                  title="Copia link cliente"
                >
                  <Copy className="w-4 h-4" />
                  Link
                </button>
              )}
              
              <button
                onClick={handleSaveDraft}
                disabled={isSaving}
                className="px-4 py-2 bg-zinc-800 border border-zinc-700 text-sm rounded-lg hover:bg-zinc-700 flex items-center gap-2 disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Salva
              </button>
              
              {proposal?.status === 'draft' && (
                <button
                  onClick={handleSendToClient}
                  disabled={isSending || renders.length === 0}
                  className="px-5 py-2 bg-gradient-to-r from-[#B08B5C] to-[#C8A36A] text-black font-medium text-sm rounded-lg hover:brightness-110 flex items-center gap-2 disabled:opacity-50"
                >
                  {isSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Invia al Cliente
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* LEFT PANEL */}
        <aside className={`flex-shrink-0 border-r border-zinc-800/50 bg-[#0A0A0B]/50 transition-all duration-300 ${
          leftCollapsed ? 'w-12' : 'w-80'
        }`}>
          <div className="h-full flex flex-col">
            {/* Panel Toggle */}
            <button
              onClick={() => setLeftCollapsed(!leftCollapsed)}
              className="p-3 border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors flex items-center justify-center"
            >
              {leftCollapsed ? (
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              ) : (
                <ChevronLeft className="w-4 h-4 text-zinc-500" />
              )}
            </button>
            
            {!leftCollapsed && (
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                
                {/* Project Info */}
                <section>
                  <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                    Progetto
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Tipo</span>
                      <span className="text-zinc-300 capitalize">{proposal?.project_type || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Creato</span>
                      <span className="text-zinc-300">
                        {proposal?.created_at ? new Date(proposal.created_at).toLocaleDateString('it-IT') : 'N/A'}
                      </span>
                    </div>
                    {proposal?.expires_at && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Scadenza</span>
                        <span className="text-zinc-300">
                          {new Date(proposal.expires_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                    )}
                  </div>
                </section>
                
                {/* Client Info */}
                <section>
                  <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                    Cliente
                  </h3>
                  <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800/50">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B08B5C] to-[#C8A36A] flex items-center justify-center">
                        <User className="w-5 h-5 text-black" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{proposal?.client_name || 'Cliente'}</p>
                        <p className="text-xs text-zinc-500">{proposal?.client_email}</p>
                      </div>
                    </div>
                    {proposal?.client_phone && (
                      <p className="text-sm text-zinc-400">{proposal.client_phone}</p>
                    )}
                  </div>
                </section>
                
                {/* Materials Used */}
                <section>
                  <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                    Materiali ({renders.length})
                  </h3>
                  <div className="space-y-2">
                    {renders.length > 0 ? (
                      [...new Set(renders.map(r => r.material_name))].map((name, i) => (
                        <div key={i} className="bg-zinc-900/50 rounded-lg p-3 border border-zinc-800/50 flex items-center gap-3">
                          <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center">
                            <Gem className="w-5 h-5 text-[#B08B5C]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{name}</p>
                            <p className="text-xs text-zinc-500 capitalize">
                              {renders.find(r => r.material_name === name)?.material_category || 'Stone'}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-zinc-500 italic">Nessun render collegato</p>
                    )}
                  </div>
                </section>
                
                {/* Advisor Notes */}
                {advisors.length > 0 && (
                  <section>
                    <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                      Advisor Consigliati
                    </h3>
                    <div className="space-y-2">
                      {advisors.slice(0, 2).map((advisor) => (
                        <div key={advisor.id} className="bg-zinc-900/50 rounded-lg p-3 border border-zinc-800/50">
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-2 h-2 rounded-full ${
                              advisor.online_status === 'online' ? 'bg-green-400' : 'bg-zinc-500'
                            }`} />
                            <p className="text-sm font-medium text-white">{advisor.full_name}</p>
                          </div>
                          <p className="text-xs text-zinc-500">{advisor.title}</p>
                          {advisor._match_reasons && (
                            <p className="text-xs text-[#B08B5C] mt-1">{advisor._match_reasons[0]}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                
              </div>
            )}
          </div>
        </aside>
        
        {/* CENTER PANEL - Render Viewer */}
        <main className="flex-1 flex flex-col overflow-hidden">
          
          {/* Render Display */}
          <div className="flex-1 p-6 overflow-y-auto">
            {renders.length > 0 && currentRender ? (
              <div className="max-w-4xl mx-auto space-y-6">
                
                {/* Main Render Image */}
                <div 
                  className="relative aspect-[16/10] bg-zinc-900 rounded-xl overflow-hidden group cursor-pointer border border-zinc-800/50"
                  onClick={() => setShowFullscreen(true)}
                >
                  <img 
                    src={showBefore ? currentRender.source_image_url : currentRender.render_url}
                    alt={showBefore ? "Prima" : "Dopo"}
                    className="w-full h-full object-cover transition-all duration-500"
                    onError={(e) => {
                      e.target.src = '/placeholder-render.jpg';
                    }}
                  />
                  
                  {/* Before/After Toggle */}
                  <div className="absolute bottom-4 left-4 flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowBefore(false); }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        !showBefore 
                          ? 'bg-[#B08B5C] text-black' 
                          : 'bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm'
                      }`}
                    >
                      Dopo
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowBefore(true); }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        showBefore 
                          ? 'bg-[#B08B5C] text-black' 
                          : 'bg-black/60 text-white hover:bg-black/80 backdrop-blur-sm'
                      }`}
                    >
                      Prima
                    </button>
                  </div>
                  
                  {/* Material Badge */}
                  <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <p className="text-sm font-medium">{currentRender.material_name}</p>
                    <p className="text-xs text-zinc-400 capitalize">{currentRender.surface_type || 'Countertop'}</p>
                  </div>
                  
                  {/* Version Badge */}
                  <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-lg flex items-center gap-2">
                    <GitBranch className="w-3.5 h-3.5 text-[#B08B5C]" />
                    <span className="text-xs font-medium">v{currentRender.version_number || 1}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      currentRender.version_type === 'approved' ? 'bg-green-500/20 text-green-400' :
                      currentRender.version_type === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      currentRender.version_type === 'draft' ? 'bg-zinc-700 text-zinc-300' :
                      'bg-[#B08B5C]/20 text-[#B08B5C]'
                    }`}>
                      {currentRender.version_type || 'draft'}
                    </span>
                  </div>
                  
                  {/* Fullscreen */}
                  <button 
                    className="absolute bottom-4 right-4 p-2 bg-black/60 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); setShowFullscreen(true); }}
                  >
                    <Maximize2 className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Render Navigation */}
                {renders.length > 1 && (
                  <div className="flex items-center justify-center gap-6">
                    <button
                      onClick={() => setActiveRenderIndex(i => Math.max(0, i - 1))}
                      disabled={activeRenderIndex === 0}
                      className="p-2 rounded-full bg-zinc-800/50 border border-zinc-700 disabled:opacity-30 hover:bg-zinc-700 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex gap-2">
                      {renders.map((render, i) => (
                        <button
                          key={render.id || i}
                          onClick={() => setActiveRenderIndex(i)}
                          className={`transition-all ${
                            i === activeRenderIndex 
                              ? 'w-8 h-2 bg-[#B08B5C] rounded-full' 
                              : 'w-2 h-2 bg-zinc-600 rounded-full hover:bg-zinc-500'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <button
                      onClick={() => setActiveRenderIndex(i => Math.min(renders.length - 1, i + 1))}
                      disabled={activeRenderIndex === renders.length - 1}
                      className="p-2 rounded-full bg-zinc-800/50 border border-zinc-700 disabled:opacity-30 hover:bg-zinc-700 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                {/* Version Timeline */}
                <RenderVersionTimeline 
                  renders={renders} 
                  activeIndex={activeRenderIndex}
                  onSelect={setActiveRenderIndex}
                />
                
              </div>
            ) : (
              /* Empty State */
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-md">
                  <div className="w-20 h-20 rounded-full bg-zinc-900 flex items-center justify-center mx-auto mb-6">
                    <ImageIcon className="w-10 h-10 text-zinc-700" />
                  </div>
                  <h3 className="text-xl font-light text-zinc-300 mb-2">Nessun Render</h3>
                  <p className="text-zinc-500 mb-6">
                    Aggiungi render dalla pagina SRE per includerli in questa proposta.
                  </p>
                  <Link
                    to="/sre-v2"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#B08B5C] to-[#C8A36A] text-black font-medium rounded-lg hover:brightness-110"
                  >
                    <Plus className="w-5 h-5" />
                    Crea Render
                  </Link>
                </div>
              </div>
            )}
          </div>
          
        </main>
        
        {/* RIGHT PANEL */}
        <aside className={`flex-shrink-0 border-l border-zinc-800/50 bg-[#0A0A0B]/50 transition-all duration-300 ${
          rightCollapsed ? 'w-12' : 'w-80'
        }`}>
          <div className="h-full flex flex-col">
            {/* Panel Toggle */}
            <button
              onClick={() => setRightCollapsed(!rightCollapsed)}
              className="p-3 border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors flex items-center justify-center"
            >
              {rightCollapsed ? (
                <ChevronLeft className="w-4 h-4 text-zinc-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              )}
            </button>
            
            {!rightCollapsed && currentRender && (
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                
                {/* Quality Metrics */}
                <section>
                  <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                    Qualità Render
                  </h3>
                  <QualityMetricsPanel render={currentRender} />
                </section>
                
                {/* Material DNA */}
                <section>
                  <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                    Material DNA
                  </h3>
                  <MaterialDNAPanel materialId={currentRender.material_id} />
                </section>
                
                {/* Render Actions */}
                <section>
                  <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                    Azioni
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={handleCreateNewVersion}
                      className="w-full py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Nuova Versione
                    </button>
                    
                    <button
                      onClick={() => navigate(`/sre-v2?material=${currentRender.material_id}`)}
                      className="w-full py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <RefreshCcw className="w-4 h-4" />
                      Modifica in SRE
                    </button>
                    
                    <a
                      href={currentRender.render_url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Scarica Render
                    </a>
                  </div>
                </section>
                
                {/* Notes */}
                {proposal?.notes && (
                  <section>
                    <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">
                      Note Progetto
                    </h3>
                    <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800/50">
                      <p className="text-sm text-zinc-300">{proposal.notes}</p>
                    </div>
                  </section>
                )}
                
              </div>
            )}
          </div>
        </aside>
        
      </div>
      
      {/* Fullscreen Modal */}
      {showFullscreen && currentRender && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-8"
          onClick={() => setShowFullscreen(false)}
        >
          <button 
            className="absolute top-6 right-6 p-3 text-white/60 hover:text-white bg-white/10 rounded-full"
            onClick={() => setShowFullscreen(false)}
          >
            <XCircle className="w-6 h-6" />
          </button>
          <img 
            src={showBefore ? currentRender.source_image_url : currentRender.render_url}
            alt="Fullscreen"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4">
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(false); }}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                !showBefore ? 'bg-[#B08B5C] text-black' : 'bg-white/10 text-white'
              }`}
            >
              Dopo
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(true); }}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                showBefore ? 'bg-[#B08B5C] text-black' : 'bg-white/10 text-white'
              }`}
            >
              Prima
            </button>
          </div>
        </div>
      )}
      
    </div>
  );
}


// =====================================================
// SUB-COMPONENTS
// =====================================================

function StatusBadge({ status }) {
  const config = {
    draft: { label: 'Bozza', bg: 'bg-zinc-700', text: 'text-zinc-300' },
    sent: { label: 'Inviata', bg: 'bg-[#B08B5C]/20', text: 'text-[#B08B5C]', border: 'border-[#B08B5C]/30' },
    viewed: { label: 'Visualizzata', bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
    approved: { label: 'Approvata', bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    rejected: { label: 'Rifiutata', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
    revision_requested: { label: 'Revisione', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' }
  };
  
  const c = config[status] || config.draft;
  
  return (
    <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${c.bg} ${c.text} ${c.border ? `border ${c.border}` : ''}`}>
      {c.label}
    </span>
  );
}


function RenderVersionTimeline({ renders, activeIndex, onSelect }) {
  if (renders.length <= 1) return null;
  
  return (
    <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
      <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-4 flex items-center gap-2">
        <GitBranch className="w-4 h-4" />
        Timeline Versioni
      </h4>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-px bg-zinc-800" />
        
        <div className="space-y-3">
          {renders.map((render, i) => (
            <button
              key={render.id || i}
              onClick={() => onSelect(i)}
              className={`w-full flex items-center gap-4 p-3 rounded-lg transition-all text-left ${
                i === activeIndex 
                  ? 'bg-[#B08B5C]/10 border border-[#B08B5C]/30' 
                  : 'hover:bg-zinc-800/50'
              }`}
            >
              {/* Timeline dot */}
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                render.version_type === 'approved' ? 'bg-green-500/20 border-2 border-green-500' :
                render.version_type === 'rejected' ? 'bg-red-500/20 border-2 border-red-500' :
                i === activeIndex ? 'bg-[#B08B5C] border-2 border-[#B08B5C]' :
                'bg-zinc-800 border-2 border-zinc-700'
              }`}>
                <span className="text-xs font-medium">{render.version_number || i + 1}</span>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white truncate">
                    {render.material_name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    render.version_type === 'approved' ? 'bg-green-500/20 text-green-400' :
                    render.version_type === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    render.version_type === 'alternative' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>
                    {render.version_type || 'draft'}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                  {render.overall_score && (
                    <span className="flex items-center gap-1">
                      <BarChart3 className="w-3 h-3" />
                      {Math.round(render.overall_score)}%
                    </span>
                  )}
                  {render.created_at && (
                    <span>
                      {new Date(render.created_at).toLocaleDateString('it-IT', { 
                        day: 'numeric', 
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function QualityMetricsPanel({ render }) {
  if (!render) return null;
  
  const metrics = [
    { key: 'realism_score', label: 'Realismo', icon: Sparkles },
    { key: 'edge_quality_score', label: 'Bordi', icon: Layers },
    { key: 'lighting_score', label: 'Illuminazione', icon: Zap },
    { key: 'shadow_score', label: 'Ombre', icon: Eye },
    { key: 'texture_score', label: 'Texture', icon: Droplets }
  ];
  
  const classification = render.quality_classification || 
    (render.overall_score >= 80 ? 'believable' : 
     render.overall_score >= 60 ? 'acceptable' : 'obviously_fake');
  
  const classConfig = {
    believable: { label: 'Credibile', color: 'text-green-400', bg: 'bg-green-500/10' },
    acceptable: { label: 'Accettabile', color: 'text-amber-400', bg: 'bg-amber-500/10' },
    obviously_fake: { label: 'Migliorabile', color: 'text-red-400', bg: 'bg-red-500/10' }
  };
  
  const cls = classConfig[classification] || classConfig.acceptable;
  
  return (
    <div className="space-y-4">
      {/* Classification Badge */}
      <div className={`rounded-lg p-4 ${cls.bg} border border-zinc-800/50`}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-400">Classificazione</span>
          <span className={`text-lg font-medium ${cls.color}`}>{cls.label}</span>
        </div>
        <div className="mt-2 h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              classification === 'believable' ? 'bg-green-500' :
              classification === 'acceptable' ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${render.overall_score || 50}%` }}
          />
        </div>
        <p className="text-xs text-zinc-500 mt-2 text-right">
          {Math.round(render.overall_score || 50)}% complessivo
        </p>
      </div>
      
      {/* Individual Metrics */}
      <div className="space-y-2">
        {metrics.map(({ key, label, icon: Icon }) => {
          const value = render[key] || 0;
          return (
            <div key={key} className="flex items-center gap-3">
              <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-zinc-400">{label}</span>
                  <span className="text-zinc-300">{Math.round(value)}%</span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      value >= 80 ? 'bg-green-500' :
                      value >= 60 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${value}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function MaterialDNAPanel({ materialId }) {
  const [dna, setDna] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (materialId) {
      loadMaterialDNA();
    }
  }, [materialId]);
  
  const loadMaterialDNA = async () => {
    try {
      const res = await fetch(`${API_URL}/api/materials/${materialId}`);
      if (res.ok) {
        const data = await res.json();
        setDna(data.dna || data.material_dna || null);
      }
    } catch (err) {
      console.error('DNA load error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
      </div>
    );
  }
  
  if (!dna) {
    return (
      <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800/50 text-center">
        <p className="text-sm text-zinc-500">DNA non disponibile</p>
      </div>
    );
  }
  
  const dnaMetrics = [
    { key: 'movement', label: 'Movimento', value: dna.movement },
    { key: 'warmth', label: 'Calore', value: dna.warmth },
    { key: 'luxury_index', label: 'Lusso', value: dna.luxury_index },
    { key: 'visual_energy', label: 'Energia', value: dna.visual_energy },
    { key: 'translucency', label: 'Traslucenza', value: dna.translucency }
  ].filter(m => m.value !== undefined);
  
  return (
    <div className="space-y-3">
      {dnaMetrics.map(({ key, label, value }) => (
        <div key={key} className="flex items-center justify-between">
          <span className="text-sm text-zinc-400">{label}</span>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#B08B5C] rounded-full"
                style={{ width: `${(value || 0) * 10}%` }}
              />
            </div>
            <span className="text-xs text-zinc-300 w-6">{value || 0}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
